/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

import React from 'react';
import OrgField from './OrgField';

/**
 * Two shapes of the same idea: pick exactly one.
 *
 * Both are <button aria-pressed> rather than radios. A radio group is the
 * textbook answer, but these are large tap targets carrying an icon, a title
 * and a description, and styling a real radio to look like one means hiding the
 * input and losing the focus ring with it. Buttons keep keyboard focus, and
 * `aria-pressed` says the same thing to a screen reader.
 *
 * OrgChips  — a compact row, for short mutually exclusive values.
 * OrgCardPicker — two descriptive cards, for a choice worth explaining.
 */

export function OrgChips({ label, value, onChange, options, required, error, help }) {
  return (
    <OrgField label={label} required={required} error={error} help={help}>
      {({ describedBy }) => (
        <div className="orgf-chips" aria-describedby={describedBy}>
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className="orgf-chip"
              aria-pressed={value === option.value}
              onClick={() => onChange(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </OrgField>
  );
}

export function OrgCardPicker({
  label, value, onChange, options, suggested, suggestionNote, required, error, help,
}) {
  return (
    <OrgField label={label} required={required} error={error} help={help}>
      {({ describedBy }) => (
        <div className="orgf-pick" aria-describedby={describedBy}>
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className="orgf-opt"
              aria-pressed={value === option.value}
              onClick={() => onChange(option.value)}
            >
              {suggested === option.value && suggestionNote && (
                <span className="orgf-sugg">{suggestionNote}</span>
              )}
              <span className="orgf-ico" aria-hidden="true">{option.icon}</span>
              <span>
                <b>{option.title}</b>
                <span>{option.description}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </OrgField>
  );
}
